import { useTranslation } from "react-i18next";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import type { TAnalyticsByDay } from "@/types/TAnalytics";

type TrendChartProps = {
  brokerData: TAnalyticsByDay[];
  claimData: TAnalyticsByDay[];
};

export function TrendChart({ brokerData, claimData }: TrendChartProps) {
  const { t } = useTranslation();

  // Merge data by date
  const allDates = new Set([
    ...brokerData.map(d => d.date),
    ...claimData.map(d => d.date),
  ]);

  const chartData = Array.from(allDates)
    .sort()
    .map(date => {
      const broker = brokerData.find(d => d.date === date);
      const claim = claimData.find(d => d.date === date);
      return {
        date: new Date(date).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" }),
        broker: broker?.total || 0,
        claim: claim?.total || 0,
      };
    });

  if (chartData.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
          {t("analytics.trend.title", "Nutzungstrend")}
        </h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          {t("analytics.noData", "Keine Daten verfügbar")}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
      <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
        {t("analytics.trend.title", "Nutzungstrend")}
      </h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
            <XAxis 
              dataKey="date" 
              tick={{ fontSize: 12 }}
              className="text-gray-600 dark:text-gray-400"
            />
            <YAxis 
              tick={{ fontSize: 12 }}
              className="text-gray-600 dark:text-gray-400"
            />
            <Tooltip
              contentStyle={{
                backgroundColor: "var(--tooltip-bg, #fff)",
                border: "1px solid var(--tooltip-border, #e5e7eb)",
                borderRadius: "0.5rem",
              }}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="broker"
              name={t("analytics.trend.broker", "Berater")}
              stroke="#3b82f6"
              strokeWidth={2}
              dot={{ fill: "#3b82f6", r: 3 }}
              activeDot={{ r: 5 }}
            />
            <Line
              type="monotone"
              dataKey="claim"
              name={t("analytics.trend.claimStatus", "Schadenstatus")}
              stroke="#10b981"
              strokeWidth={2}
              dot={{ fill: "#10b981", r: 3 }}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
